import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { v4 as uuidv4 } from 'uuid';
import { INewCompanyRequestService,
         IOpsViewerMessagesService,
         IApplicationContextService,
         } from '../../../../services/services';
import { NewCompanyRequest,
         RequestStatuses,
         CompanyTypes,
         UnitSystemChoice,
         CompanyWorkbookConfiguration,
         WellKnownMeasurementChoice} from '../../../../models/models';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Address, User, CompanyWorkbookConfigurationClient, UserClient } from '../../../../services/nswag/nswagclient';

@Component({
  selector: 'app-new-company-request',
  
  standalone: false,
  templateUrl: './new-company-request.component.html',
  styleUrls: ['./new-company-request.component.css']
})
export class NewCompanyRequestComponent implements OnInit, OnChanges {

  @Input() user: User | null = null;
  @Input() dialogOnly = false;
  @Output() error = new EventEmitter<unknown>();
  private _user: User | null = null;
  userReady = false;
  requests: NewCompanyRequest[] = [];
  requestCols: any[] = [];
  _requestsReady = false;
  _requestsNamesUpdated = false;
  unitSystemChoices: SelectItem[] = [
    {label: UnitSystemChoice.si, value: UnitSystemChoice.si  },
    {label: UnitSystemChoice.imperial, value: UnitSystemChoice.imperial  }
  ];
  companyTypes: SelectItem[] = [
    { label: CompanyTypes.Consumer, value: CompanyTypes.Consumer },
    { label: CompanyTypes.Vendor, value: CompanyTypes.Vendor }
  ];
  loading = true;

  showRejectDialog = false;
  _requestToReject: NewCompanyRequest | null = null;
  rejectReason: string | null = null;

  showAddNewCompanyRequest = false;
  requestToAdd = new NewCompanyRequest();
  requestToAddAutoApprovedEmailDomains: string | null = null;
  requestToAddModelValid = true;
  isShippingSameAsBilling = true;
  billingEmailAddressValid = true;
  shippingEmailAddressValid = true;

  constructor(
    private _newCompanyRequestService: INewCompanyRequestService,
    private _userClient: UserClient,
  private _confirmationService: ConfirmationService,
  private _messageService: IOpsViewerMessagesService,
  private _companyWorkbookConfigurationClient: CompanyWorkbookConfigurationClient,
    private _applicationContextService: IApplicationContextService,
    private _router: Router
  ) { }

  ngOnInit() {
    this.setRequestCols();
  }

  ngOnChanges() {
    if (this.user && (this._user !== this.user)) {
      this._user = this.user;
      this.userReady = true;
      this.getRequests();
      if (this.dialogOnly) {
        this.addRequest();
      }
    }
  }

  setRequestCols() {
    this.requestCols = [
      { field: 'companyName', header: 'Company' },
      { field: 'companyType', header: 'Type' },
      { field: 'requestedByName', header: 'Requested By' },
      { field: 'status', header: 'Status' },
      { field: '', header: '' },
    ];
  }

  getRequests() {
    this._requestsReady = false;
    this._newCompanyRequestService.getNewCompanyRequests().subscribe(
      results => {
        if (results && results.length > 0) {
          this.requests = results;
          this.setRequestedByNames();
        } else {
          this._requestsNamesUpdated = true;
        }
        this._requestsReady = true;
        this.checkIfEvertythingReady();
      },
      error => {
        this.handleError(error, 'Could not get New Company Requests');
      }
    );
  }

  setRequestedByNames() {
    this._requestsNamesUpdated = false;
    const distinctUserIds = this.getUserIdsToGetNamesFor();
    this.addRequestedByNameToRequests(distinctUserIds);
  }

  getUserIdsToGetNamesFor(): string[] {
    const distinctUserIds: string[] = [];
    this.requests.map(r => {
      if (r.requestedById && !distinctUserIds.includes(r.requestedById)) {
        distinctUserIds.push(r.requestedById);
      }
    });
    return distinctUserIds;
  }

  addRequestedByNameToRequests(distinctUserIds: string[]) {
    let requestedNamesUpdated = 0;
    distinctUserIds.map(u => {
      this._userClient.getUserName(u).subscribe(
        result => {
          if (result) {
            this.requests.map(r => {
              if (r.requestedById === u) {
                r.requestedByName = result.userName ?? null;
              }
            });
          }
          requestedNamesUpdated++;
          if (requestedNamesUpdated === distinctUserIds.length) {
            this._requestsNamesUpdated = true;
            this.checkIfEvertythingReady();
          }
        },
        error => {
          this.handleError(error, 'Could not get New Company Requests');
        }
      );
    });
  }

  checkIfEvertythingReady() {
    this.loading = this._requestsReady && this._requestsNamesUpdated;
    if (this.loading) {
      this.showAddNewCompanyRequest = this.dialogOnly;
    }
  }

  confirmApprove(request: NewCompanyRequest) {
    this._confirmationService.confirm({
      key: 'confirmationDialog',
      header: 'Approve Confirmation',
      message: 'Are you sure you want to Approve' + request.companyName + ' ?',
      icon: 'fa fa-question-circle',
      accept: () => { this.approve(request); }
    });
  }

  approve(request: NewCompanyRequest) {
    this._applicationContextService.showApplicationSpinner(false);
    this._newCompanyRequestService.approveNewCompanyRequest(request.id).subscribe(
      async (_result: unknown) => {
        request.status = RequestStatuses.Approved;
        await this.createNewCompanyWorkbookConfigurationOnceApproved(request);
        this._applicationContextService.hideApplicationSpinner();
        this._messageService.successMessage('New Company Request successfully Approved.', '');
        // TODO: Create default workbook configuration
        this._applicationContextService.alertRequestsUpdated();
        this._applicationContextService.alertCompaniesListUpdated();
      },
      (error: unknown) => {
        this._applicationContextService.hideApplicationSpinner();
        this.handleError(error, 'Unable to Approve Requeest');
      }
    );
  }

  async createNewCompanyWorkbookConfigurationOnceApproved(request: NewCompanyRequest) {
    if (request.companyType === CompanyTypes.Vendor && request.companyId) {
      const configuration = new CompanyWorkbookConfiguration('', request.companyId);
      const workbookActivities = await firstValueFrom(this._companyWorkbookConfigurationClient.getWorkbookActivityOptions()).catch(
        error => {
          this.handleError(error, 'Could not Create Company Configuration');
          return;
        });
      const apiWkms = await firstValueFrom(this._companyWorkbookConfigurationClient.getWellKnownMeasurements())
        .catch(
          error => {
            this.handleError(error, 'Could not Create Company Configuration');
            return;
          });
      const wkms = (apiWkms || []).map(r => ({
        name: r.name ?? '',
        displayName: r.displayName ?? (r.name ?? ''),
        siUnits: (r.siUnits || []).map(u => ({ unitKey: u.unitKey, unitName: u.unitName })),
        imperialUnits: (r.imperialUnits || []).map(u => ({ unitKey: u.unitKey, unitName: u.unitName })),
        defaultSiUnit: r.defaultSiUnit ? { unitKey: r.defaultSiUnit.unitKey, unitName: r.defaultSiUnit.unitName } : null,
        defaultImperialUnit: r.defaultImperialUnit ? { unitKey: r.defaultImperialUnit.unitKey, unitName: r.defaultImperialUnit.unitName } : null
      }));
      configuration.setDefaultMeasurements(
        workbookActivities as string[],
        request.companyUnitSystem ?? UnitSystemChoice.imperial,
        wkms as WellKnownMeasurementChoice[]);
      await firstValueFrom(this._companyWorkbookConfigurationClient.createOrUpdate(configuration as any))
        .catch(
          error => { this.handleError(error, 'Could not Create Company Configuration'); }
        );
    }
  }

  confirmReject(request: NewCompanyRequest) {
    this.rejectReason = null;
    this.showRejectDialog = true;
    this._requestToReject = request;
  }

  cancelReject() {
    this.showRejectDialog = false;
    this._requestToReject = null;
  }

  reject() {
    if (this.rejectReason && this._requestToReject?.id) {
      this._applicationContextService.showApplicationSpinner(false);
      this._newCompanyRequestService.rejectNewCompanyRequest(this._requestToReject.id, this.rejectReason).subscribe(
        (_result: unknown) => {
          const request = this.requests.find(r => r.id === this._requestToReject?.id);
          if (request) {
            request.status = RequestStatuses.Rejected;
          }
          this.showRejectDialog = false;
          this._requestToReject = null;
          this._applicationContextService.hideApplicationSpinner();
          this._messageService.successMessage('New Company Request Rejected.', '');
          this._applicationContextService.alertRequestsUpdated();
        },
        (error: unknown) => {
          this._applicationContextService.hideApplicationSpinner();
          this.handleError(error, 'Unable to Reject Request');
          this.showRejectDialog = false;
          this._requestToReject = null;
        }
      );
    }
  }

  addRequest() {
    this.requestToAddAutoApprovedEmailDomains = null;
    this.requestToAdd = new NewCompanyRequest();
    // company doesn't exist yet, but same id will be used to create company
    // when request is approved
    this.requestToAdd.companyId = uuidv4();
    this.requestToAddModelValid = true;
    this.showAddNewCompanyRequest = true;
  }

  saveAdd() {
    if (this.requestToAddAutoApprovedEmailDomains) {
      this.requestToAdd.autoApprovedEmailDomains = this.requestToAddAutoApprovedEmailDomains.split(';');
    }
    if (this.isShippingSameAsBilling) {
      this.requestToAdd.shippingAddress = this.requestToAdd.billingAddress;
    }

    this.requestToAddModelValid = this.validateAddModel();
    if (this.requestToAddModelValid) {
      this._applicationContextService.showApplicationSpinner(false);
      this._newCompanyRequestService.createNewCompanyRequest(this.requestToAdd).subscribe(
        (_result: unknown) => {
          this.showAddNewCompanyRequest = false;
          this._applicationContextService.hideApplicationSpinner();
          this._messageService.successMessage('New Company Request successfully submitted.', '');
          this._applicationContextService.alertRequestsUpdated();
          if (this.dialogOnly) {
            this.navigateHome();
          } else {
            this.getRequests();
          }
        },
        (error: unknown) => {
          this._applicationContextService.hideApplicationSpinner();
          this.handleError(error, 'Unable to create New Company Request');
        }
      );
    } else {
      this._messageService.warnMessage('Validation Error:', 'One or more required input fields is invalid.');
    }
  }

  validateAddModel(): boolean {
    if (!this.requestToAdd.companyName) { return false; }
    if (!this.requestToAdd.companyUnitSystem) { return false; }
    // if (!this.requestToAdd.companyPrefix) { return false; }
    if (!this.validateAddress(this.requestToAdd.billingAddress, this.billingEmailAddressValid)) {
      return false;
    }
    if (!this.validateAddress(this.requestToAdd.shippingAddress, this.shippingEmailAddressValid)) {
      return false;
    }

    return true;
  }

  validateAddress(address: Address, isEmailValid: boolean): boolean {
    if (!address.name) { return false; }
    if (!address.street) { return false; }
    if (!address.city) { return false; }
    if (!address.state) { return false; }
    if (!address.zip) { return false; }
    if (!address.email || !isEmailValid) { return false; }
    if (!address.phone) { return false; }

    return true;
  }

  validateBillingEmail(event: any) {
    this.billingEmailAddressValid = event;
  }

  validateShippingEmail(event: any) {
    this.shippingEmailAddressValid = event;
  }

  cancelAdd() {
    if (this.dialogOnly) {
      this.navigateHome();
    }
    this.showAddNewCompanyRequest = false;
  }

  confirmDelete(request: NewCompanyRequest) {
    this._confirmationService.confirm(
      {
        key: 'confirmationDialog',
        header: 'Delete Confirmation',
        message: 'Are you sure you want to Delete request for ' + request.companyName + ' ?',
        icon: 'fa fa-question-circle',
        accept: () => { this.delete(request); }
      }
    );
  }

  delete(request: NewCompanyRequest) {
    this._applicationContextService.showApplicationSpinner(false);
    this._newCompanyRequestService.deleteNewCompanyRequest(request.id).subscribe(
      (_result: unknown) => {
        this.requests = this.requests.filter(r => r.id !== request.id);
        this._applicationContextService.hideApplicationSpinner();
      },
      (error: unknown) => {
        this._applicationContextService.hideApplicationSpinner();
        this.handleError(error, 'Could not delete request');
      }
    );
  }

  handleError(error: any, displayMessage: string) {
    this._messageService.errorMessage(error, displayMessage);
  }

  navigateHome() {
    this._router.navigate(['/home']);
  }
}
